import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuBell, LuBoxes, LuClipboardCheck, LuCalendarClock, LuCalendarX, LuFileClock, LuFileWarning, LuIndianRupee, LuInfo, LuReceipt, LuWallet } from 'react-icons/lu';
import { repo } from '@/services';
import { invalidate, useAsync } from '@/hooks/useAsync';
import { useClickOutside } from '@/hooks/useClickOutside';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AppNotification } from '@/types/domain';

const KIND_ICON: Record<string, typeof LuBell> = {
  expiry: LuCalendarClock,
  expired: LuCalendarX,
  document_pending: LuFileWarning,
  doc_overdue: LuFileWarning,
  doc_due: LuFileClock,
  value: LuIndianRupee,
  quantity: LuBoxes,
  consumption: LuBoxes,
  approval: LuClipboardCheck,
  budget: LuWallet,
  bills: LuReceipt,
};
const SEVERITY: Record<AppNotification['severity'], string> = {
  info: 'text-brand-600 bg-brand-50',
  warning: 'text-amber-700 bg-amber-50',
  critical: 'text-red-700 bg-red-50',
};

export function NotificationsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useClickOutside(ref, () => setOpen(false), open);
  const { data } = useAsync(() => repo.notifications.listMine(), [], ['notifications']);
  const unread = data?.filter((n) => !n.readAt).length ?? 0;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        aria-expanded={open}
      >
        <LuBell className="size-5" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-[22rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            {unread > 0 && (
              <button
                className="text-xs font-medium text-brand-700 hover:underline"
                onClick={async () => {
                  await repo.notifications.markAllRead();
                  invalidate('notifications');
                }}
              >
                Mark all as read
              </button>
            )}
          </div>
          <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {!data?.length && <li className="px-4 py-6 text-center text-sm text-slate-500">You're all caught up.</li>}
            {data?.map((n) => {
              const Icon = KIND_ICON[n.kind] ?? LuInfo;
              return (
                <li key={n.id}>
                  <button
                    className={cn('flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50', !n.readAt && 'bg-brand-50/40')}
                    onClick={async () => {
                      setOpen(false);
                      if (!n.readAt) {
                        await repo.notifications.markRead(n.id);
                        invalidate('notifications');
                      }
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <span className={cn('h-fit rounded-md p-1.5', SEVERITY[n.severity])}><Icon className="size-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm', n.readAt ? 'text-slate-700' : 'font-medium text-slate-900')}>{n.title}</span>
                      {n.body && <span className="mt-0.5 block truncate text-xs text-slate-500">{n.body}</span>}
                      <span className="mt-0.5 block text-[11px] text-slate-400">{formatRelative(n.createdAt)}</span>
                    </span>
                    {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-600" aria-label="Unread" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <Link to="/alerts" onClick={() => setOpen(false)} className="block border-t border-slate-200 px-4 py-2.5 text-center text-xs font-medium text-brand-700 hover:bg-slate-50">
            Alert history (email status)
          </Link>
        </div>
      )}
    </div>
  );
}
